import { autoinject, bindable, Container, processContent, ViewCompiler, ViewResources, TaskQueue } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { inheritBindingContext, TemplateSupport } from '../../component-model/template-elements';
import { NodeModel } from '../../models/node-model';
import { SearchFormatter } from '../../utils/search-formatter';
import { IDraggableHandler, IDroppableHandler } from '../drag-and-drop/models';
import { TreeNode } from './tree-node';
import { remove, flatten } from 'lodash';
import { getScrollParent } from '../../utils/scrolling';

@autoinject
@processContent(inheritBindingContext(false))
export class TreeView extends TemplateSupport {

	@bindable nodes: NodeModel[];
	@bindable draggableHandler: IDraggableHandler;
	@bindable droppableHandler: IDroppableHandler;

	/*
	SelectMode
	==========
	- Single: Only one node can be selected at a time. Unselection is not supported.
		Selection is made by clicking the content of the node
	- Multiple: Multiple nodes can be selected/unselected by clicking their checkboxes.
		Descendants of a selected node are automatically selected.
		Also parents of selected nodes are automatically selected;
		they're shown as indeterminate if not all descendants are selected.
	- MultipleSeparately: Multiple nodes can be selected/unselected by clicking their checkboxes.
		Descendants or parents are not automatically selected/unselected.
	*/
	@bindable selectMode: 'Single' | 'Multiple' | 'MultipleSeparately' = 'Single';

	@bindable searchTerm: string;

	@bindable expandByDefault = false;
	@bindable useStickyNodeTitle = false;

	@bindable valueField: string;

	@bindable selectedValue: any;
	@bindable preSelectedValue: any;
	@bindable selectedValues: any[] = [];

	@bindable selectedNodesChanged: (params: { values: any[], selectedNodes: Set<TreeNode> }) => void;

	private treeNodes: TreeNode[] = [];
	private selectedNodes: Set<TreeNode>  = new Set<TreeNode>();
	private internalValueUpdateInProgress: boolean = false;

	constructor(
		private taskQueue: TaskQueue,
		private ea: EventAggregator,
		element: Element,
		container: Container,
		compiler: ViewCompiler,
		resources: ViewResources) {
		super(element, container, compiler, resources);
	}

	attached() {
		// event sent by the back button from details view (right column) to focus tree view and selected node
		this.ea.subscribe('focus-tree-view', () => {
			if (this.selectMode === 'Single' && this.selectedValue) {
				const element = document.getElementById(`wrapper-${this.selectedValue.id}`);
				if (element) {
					element.focus();
				}
			}

			if (this.selectMode !== 'Single' && this.selectedValues.length) {
				const element = document.getElementById(`wrapper-${this.selectedValues[0].id}`);
				if (element) {
					element.focus();
				}
			}
		});
	}

	selectedValueChanged(newValue: any, oldValue: any) {
		if (this.selectMode !== 'Single') {
			return;
		}

		this.preSelectedValue = null;

		this.runNotificationAction(() => {
			if (newValue !== oldValue) {
				this.selectNodesWhere(tn => this.getItemValue(tn.current) === newValue);
			}
		});
	}

	selectedValuesChanged(newValues: any[], oldValues: any[]) {
		if (this.selectMode === 'Single') {
			return;
		}

		this.runNotificationAction(() => {
			if (newValues !== oldValues) {
				this.selectNodesWhere(tn => newValues != null && newValues.indexOf(this.getItemValue(tn.current)) >= 0);
			}
		});
	}

	searchTermChanged(newVal, oldVal) {
		this.clearSearch();

		if (!this.searchTerm) {
			return;
		}

		const results = this.searchNodes(n => n.current.title.toLowerCase().indexOf(this.searchTerm.toLowerCase()) !== -1);
		this.expandNodes(results, true);

		if (results.length) {
			this.scrollNodeToView(results[0]);
		}
	}

	preSelectedValueChanged(newVal, oldVal) {
		if (!newVal) {
			// unselect preselection
			const results = this.searchNodes(n => n.current.id == oldVal?.id);
			if (results.length) {
				this.unselectNode(results[0]);
			}

			return;
		}

		const results = this.searchNodes(n => n.current.id == newVal.id);
		this.expandNodes(results, false);

		if (results.length) {
			this.scrollNodeToView(results[0]);
		}
	}

	addRootNode(node: TreeNode): void {
		this.treeNodes.push(node);
	}

	removeRootNode(node: TreeNode): void {
		remove(this.treeNodes, n => n === node);
	}

	nodeUnbound(node: TreeNode): void {
		if (this.disposing) { return; }

		if (node.isSelected) {
			this.unselectNode(node);
		}
	}

	private disposing = false;

	detached() {
		this.disposing = true;
	}

	selectNode(node: TreeNode): void {
		this.suppressChangeNotifications(() => {

			if (node.isSelected === true) { return; }

			node.isSelected = true;

			if (this.selectMode === 'Single') {
				this.selectedNodes.forEach(n => n.isSelected = false);
				this.selectedNodes.clear();
				this.selectedNodes.add(node);
			} else {
				this.selectedNodes.add(node);
			}

			this.setSelectedValues(this.getSelectedValues());
			this.updateSingleValue();
		});
	}

	private setSelectedValues(values: any[]) {
		this.selectedValues = values;
		if (this.selectedNodesChanged) {
			this.selectedNodesChanged({ values: values, selectedNodes: this.selectedNodes });
		}
	}

	unselectNode(node: TreeNode): void {
		this.suppressChangeNotifications(() => {
			node.isSelected = false;

			this.selectedNodes.delete(node);

			this.setSelectedValues(this.getSelectedValues());
			this.updateSingleValue();
		});
	}

	selectNodes(nodes: TreeNode[]): void {
		this.suppressChangeNotifications(() => {
			for (const node of nodes) {
				this.selectNode(node);
			}
		});
	}

	unselectNodes(nodes: TreeNode[]): void {
		this.suppressChangeNotifications(() => {
			for (const node of nodes) {
				this.unselectNode(node);
			}
		});
	}

	private clearSearch() {
		this.getAllNodes().forEach(n => {
			n.isSearchResult = false;
			n.isExpanded = false;
			n.current.displayText = n.current.title;
		});
	}

	private expandNodes(nodes: TreeNode[], isSearchResult: boolean) {
		nodes.forEach(n => {
			n.isSearchResult = isSearchResult;
			n.isSelected = !isSearchResult;
			n.current.displayText = isSearchResult ? SearchFormatter.formatSearch(n.current.title, this.searchTerm) : n.current.displayText;
			n.expandParents();
		});
	}

	private scrollNodeToView(node: TreeNode) {
		const target: Element = node.nodeTitleElement;
		target.scrollIntoView();
		const scrollParent = getScrollParent(target, false);
		if (!scrollParent || scrollParent.localName === "body" || scrollParent.localName === "html") {
			const navbar = document.getElementsByClassName("navbar");
			if (navbar.length) {
				document.scrollingElement.scrollTop -= navbar[0].getBoundingClientRect().bottom + 5;
			}
		}
	}

	/**
	 * Returns the values corresponding to the current set of selected nodes
	 */
	private getSelectedValues(): any[] {
		const result = [];
		this.selectedNodes.forEach((node: TreeNode) => result.push(this.getItemValue(node.current)));
		return result;
	}

	/**
	 * If valueField is set, returns the value of valueField for this item.
	 * Otherwise, returns the item itself.
	 * 
	 * @param item
	 */
	private getItemValue(item: any) {
		return this.valueField ? item[this.valueField] : item;
	}

	/**
	 * If this tree is in single-select mode, updates the current single selected value
	 * from the array of all selected values.
	 */
	private updateSingleValue(): void {
		if (this.selectMode !== 'Single') {
			return;
		}

		const value = this.selectedValues.length > 0
			? this.selectedValues[0]
			: null;

		this.selectedValue = value;
	}

	/**
	 * Performs the specified action only if there is no internal
	 * update currently in progress. This is intended as a guard for
	 * e.g. change event handlers that might be triggered by an internal
	 * update.
	 * 
	 * @param action
	 */
	private runNotificationAction(action: () => void): void {
		if (this.internalValueUpdateInProgress) {
			return;
		}

		action();
	}

	/**
	 * Performs an internal update action and attempts to flush related
	 * delayed binding actions by flushing the microtask queue. During the update,
	 * actions performed via runNotificationAction will be suppressed. This mechanism exists to
	 * allow for more complex two-way binding scenarios without causing infinite loops.
	 *
	 * @param update The update action to perform
	 */
	private suppressChangeNotifications(update: () => void): void {
		// Nested call, no need to update the flag
		if (this.internalValueUpdateInProgress) {
			update();
			return;
		}

		this.internalValueUpdateInProgress = true;
		update();

		// Queuing the flag because otherwise microtasks would cause funky behaviour when tree multiselect is in use
		this.taskQueue.queueMicroTask(() => this.internalValueUpdateInProgress = false);
		
	}

	/**
	 * Searches the tree for nodes that match predicate and sets the resulting
	 * nodes as selected.
	 * 
	 * @param predicate
	 */
	private selectNodesWhere(predicate: (node: TreeNode) => boolean) {
		this.suppressChangeNotifications(() => {
			let matches = this.searchNodes(predicate);

			if (!matches.length) {
				/**
				 * If predicate search results are empty we'll fallback to searching selected nodes by node.id.
				 */
				matches = this.searchNodes(node => this.selectedValues != null && this.selectedValues.indexOf(node.current.id) >= 0);
			}

			this.selectedNodes.forEach(n => n.isSelected = false);
			this.selectedNodes.clear();

			for (const node of matches) {
				node.isSelected = true;
				if (this.selectMode === 'Single') {
					node.expandParents();
				}

				this.selectedNodes.add(node);

				// collectNodes returns children before their parents
				// so that we can update the indeterminate status here
				// without doing an additional pass over the node list
				node.updateIndeterminateStatus();

				if (this.selectMode === 'Single') {
					break;
				}
			}

			this.selectedValues = this.getSelectedValues();
			this.updateSingleValue();
		});
	}

	/**
	 * Returns a flat list of all nodes in this tree for which predicate returns true.
	 * 
	 * @param nodes
	 * @param predicate
	 */
	private searchNodes(predicate: (node: TreeNode) => boolean): TreeNode[] {
		if (!this.treeNodes) {
			return [];
		}

		return this.getAllNodes().filter(predicate);
	}

	/**
	 * Returns a flat list of all nodes in this tree.
	 */
	private getAllNodes(): TreeNode[] {
		return flatten(this.treeNodes.map(n => n.flattenSubtree()));
	}
}
